import { pushContext } from '../core/context'
import { runWithComponent, stopComponentEffects, releaseComponentGlobals, type ComponentInstance } from '../core/hook'
import { unsafeEffect, flush } from '../core/lifecycle'
import {
    PatchBuffer,
    createPatchBuffer,
    OP_INSERT_ELEMENT,
    OP_REMOVE,
} from '../core/patch-ops'
import { fnodePool } from '../core/fnode-pool'
import { componentRegistry } from '../core/component-registry'
import { batchInsertBefore } from './batch-insert'
import { batchRemoveRange } from './batch-remove'
import type { FNode, FNodeChild } from './types'

// Module-level patch buffer — reset and replayed each flush.
// Phase 2A wiring; Phase 2B batch helpers will consume this buffer.
const _patchBuffer: PatchBuffer = createPatchBuffer()

/** Expose for testing / Phase B consumers. */
export function getPatchBuffer(): PatchBuffer {
    return _patchBuffer
}

export function resetPatchBuffer(): void {
    _patchBuffer.reset()
}

// Extended ComponentInstance for DOM tracking.
// Phase 5C: every property is pre-initialized in the constructor literal to
// lock the V8 hidden class. Optional fields use `| null` as the unallocated
// sentinel rather than relying on `undefined`/lazy creation.
interface DOMComponentInstance extends ComponentInstance {
    nodes: Node[]
    parent: HTMLElement
    fnode: FNode
    props: Record<string, unknown>
    key: unknown
    renderFn: (() => void) | null
    children: Set<DOMComponentInstance>
    parentInstance: DOMComponentInstance | null
    disposed: boolean
    /** Stable key under which this instance is registered with the componentRegistry. */
    __registryKey: string | null
    /** Guard: prevents the FinalizationRegistry cleanup from re-running our explicit teardown. */
    __emergencyCleanupDone: boolean
}

let _instanceKeyCounter = 0
function nextInstanceRegistryKey(): string {
    return `flex-inst-${++_instanceKeyCounter}`
}

// Registry to store component instances by parent and key
const instanceRegistry = new WeakMap<HTMLElement, Map<unknown, DOMComponentInstance>>()

// Current rendering component instance (for tracking parent-child relationships)
let currentRenderingInstance: DOMComponentInstance | null = null

// Reset render state - call before re-rendering the entire app (e.g., srcdoc update)
export function resetRender(): void {
    currentRenderingInstance = null
    // Note: WeakMap will automatically GC old entries when their keys are gone
}

// Recursively remove component instance and all its children
function removeComponentInstance(instance: DOMComponentInstance): void {
    if (instance.disposed) return
    instance.disposed = true

    // First, recursively remove all children
    instance.children.forEach(child => {
        removeComponentInstance(child)
    })

    // Mark emergency-cleanup as done BEFORE unregistering — the finalizer
    // callback we registered checks this flag, so any later GC-triggered fire
    // becomes a no-op. Explicit unregister also removes the FinalizationRegistry
    // binding, so under normal flow the callback never runs.
    instance.__emergencyCleanupDone = true
    if (instance.__registryKey) {
        componentRegistry.unregister(instance.__registryKey)
    }

    // Stop any reactive effects registered by unsafeEffect() inside this component's render.
    // This prevents memory leaks from effects that track reactive state beyond the component's lifetime.
    stopComponentEffects(instance)

    // Decrement refcounts for any global registry keys held by this component and evict entries at zero.
    releaseComponentGlobals(instance)

    // Remove DOM nodes
    instance.nodes.forEach(node => {
        if (node.parentNode) {
            node.parentNode.removeChild(node)
        }
    })

    instance.nodes = []
    instance.children.clear()

    // Unlink from parent
    if (instance.parentInstance) {
        instance.parentInstance.children.delete(instance)
    }

    // Phase 4 — return the FNode descriptor that drove this component to the
    // pool. The DOM nodes it referenced are gone; the descriptor itself is
    // dead. Subsequent `f()` calls do not yet acquire from the pool, but the
    // pool now warms up across the run so future passes (Phase 4 Task 4)
    // can flip `f()` to pull from it.
    fnodePool.release(instance.fnode)
}

// Get or create registry for parent element
function getRegistry(parent: HTMLElement): Map<unknown, DOMComponentInstance> {
    if (!instanceRegistry.has(parent)) {
        instanceRegistry.set(parent, new Map())
    }
    return instanceRegistry.get(parent)!
}

// Remove instance from registry and DOM
function removeInstanceByKey(registryParent: HTMLElement, key: unknown): void {
    const registry = instanceRegistry.get(registryParent)
    if (registry && registry.has(key)) {
        const instance = registry.get(key)!
        removeComponentInstance(instance)
        registry.delete(key)
    }
}

/**
 * Build a flat props object for a component invocation, lifting `children`
 * out of the FNode children array as a top-level prop the component can read.
 *
 * Phase 4 — module-scope static function. Previously this lived as an arrow
 * inside renderComponent and allocated a fresh closure per component mount.
 */
function mergeProps(node: FNode): Record<string, unknown> {
    const props: Record<string, unknown> = { ...node.props }
    if (node.children && node.children.length > 0) {
        props.children = node.children.length === 1
            ? node.children[0]
            : node.children
    }
    return props
}

// Render a function component with reactive re-rendering
function renderComponent(fnode: FNode, parent: HTMLElement, registryParent?: HTMLElement): Node[] {
    const effectiveRegistryParent = registryParent || parent

    const hasExplicitKey = fnode.key !== undefined
    const parentRegistry = getRegistry(effectiveRegistryParent)

    let key: unknown
    if (hasExplicitKey) {
        key = fnode.key
    } else {
        let instanceCount = 0
        const componentName = typeof fnode.type === 'function' ? (fnode.type.name || 'anonymous') : 'anonymous'
        parentRegistry.forEach((_, k) => {
            if (typeof k === 'string' && k.startsWith(`__auto_${componentName}_`)) {
                instanceCount++
            }
        })
        key = `__auto_${componentName}_${instanceCount}`
    }

    // Check if we have an existing instance with this key
    if (parentRegistry.has(key)) {
        const instance = parentRegistry.get(key)!

        // If instance was marked for disposal, skip reuse
        if (instance.disposed) {
            parentRegistry.delete(key)
        } else {
            // Reuse existing instance - update props and re-render
            instance.fnode = fnode
            instance.props = mergeProps(fnode)

            // Trigger re-render
            if (instance.renderFn) {
                instance.renderFn()
            }

            return instance.nodes
        }
    }

    // Create new component instance.
    // Phase 5C: every field is pre-initialized in this literal to lock the V8
    // hidden class. Order matters — keep it stable across renders so all
    // instances share one IC entry.
    const registryKey = nextInstanceRegistryKey()
    const instance: DOMComponentInstance = {
        hooks: [],
        hookIndex: 0,
        componentName: typeof fnode.type === 'function' ? (fnode.type.name || 'Component') : 'Component',
        hookSignatures: null,
        prevHookSignatures: null,
        effects: null,
        globalKeys: null,
        nodes: [],
        parent,
        fnode: fnode,
        props: mergeProps(fnode),
        key,
        renderFn: null,
        children: new Set(),
        parentInstance: currentRenderingInstance ?? null,
        disposed: false,
        __registryKey: registryKey,
        __emergencyCleanupDone: false,
    }

    if (currentRenderingInstance) {
        currentRenderingInstance.children.add(instance)
    }

    parentRegistry.set(key, instance)

    // Phase 4 — register the instance with ComponentWeakRegistry so a forgotten
    // dispose path (or GC-only collection) still releases its effects + globals.
    // The flag guard is closed-over per instance, so under normal unmount the
    // cleanup runs exactly once via removeComponentInstance.
    componentRegistry.register(instance, registryKey, () => {
        if (instance.__emergencyCleanupDone) return
        instance.__emergencyCleanupDone = true
        stopComponentEffects(instance)
        releaseComponentGlobals(instance)
    })

    let isFirstRender = true

    const renderFn = () => {
        if (instance.disposed) return

        const currentFnode = instance.fnode
        const currentProps = instance.props

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ._contextId is a dynamic property set by Context constructor on Provider functions
        const isProvider = (currentFnode.type as any)._contextId !== undefined
        if (isProvider) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see above
            pushContext((currentFnode.type as any)._contextId as symbol, currentProps.value)
        }

        const previousRenderingInstance = currentRenderingInstance
        currentRenderingInstance = instance

        const fn = currentFnode.type as (props: Record<string, unknown>) => FNodeChild
        const result = runWithComponent(instance, () => fn(currentProps))

        if (isFirstRender) {
            const newNodes = renderNode(result, parent)
            instance.nodes = newNodes ? (Array.isArray(newNodes) ? newNodes : [newNodes]) : []
            instance.nodes.forEach(node => {
                if (!(node as Node & { __ownerInstance?: DOMComponentInstance }).__ownerInstance) {
                    (node as Node & { __ownerInstance?: DOMComponentInstance }).__ownerInstance = instance
                }
            })
            isFirstRender = false
        } else {
            if (instance.nodes.length === 0) {
                const newNodes = renderNode(result, parent)
                instance.nodes = newNodes ? (Array.isArray(newNodes) ? newNodes : [newNodes]) : []
                currentRenderingInstance = previousRenderingInstance
                return
            }

            const firstNode = instance.nodes[0]
            let nodeParent = firstNode.parentNode as HTMLElement

            if (!nodeParent) {
                nodeParent = instance.parent
            }

            const marker = document.createComment('flexium-marker')
            const lastNode = instance.nodes[instance.nodes.length - 1]
            if (lastNode.nextSibling) {
                nodeParent.insertBefore(marker, lastNode.nextSibling)
            } else {
                nodeParent.appendChild(marker)
            }

            instance.children.clear()

            const tempContainer = document.createElement('div')
            const newNodes = renderNode(result, tempContainer, nodeParent)
            const newNodesArray = newNodes ? (Array.isArray(newNodes) ? newNodes : [newNodes]) : []

            const reconciledNodes = reconcile(instance.nodes, newNodesArray, nodeParent, marker)

            nodeParent.removeChild(marker)

            instance.nodes = reconciledNodes
        }

        currentRenderingInstance = previousRenderingInstance
    }

    instance.renderFn = renderFn

    // Framework-internal root effect: drives this component's reactive re-renders.
    // Use .detached so it is NOT registered on the parent component — ownership is
    // tracked via DOMComponentInstance itself and stopped in removeComponentInstance.
    unsafeEffect.detached(renderFn)

    // Flush to ensure synchronous rendering for proper node tracking
    flush()

    return instance.nodes
}

export function renderNode(fnode: FNodeChild, parent: HTMLElement, registryParent?: HTMLElement): Node | Node[] | null {
    const effectiveRegistryParent = registryParent || parent

    if (fnode === null || fnode === undefined || typeof fnode === 'boolean') {
        const node = document.createTextNode('')
        parent.appendChild(node)
        return node
    }

    if (typeof fnode === 'string' || typeof fnode === 'number') {
        const node = document.createTextNode(String(fnode))
        parent.appendChild(node)
        return node
    }

    if (Array.isArray(fnode)) {
        const registry = getRegistry(effectiveRegistryParent)

        // Collect existing keys before render
        const oldKeys = new Set<unknown>(registry.keys())

        // Pre-collect explicit keys that will be rendered
        const usedKeys = new Set<unknown>()
        fnode.forEach(child => {
            if (child && typeof child === 'object' && !Array.isArray(child) && 'type' in child && typeof (child as FNode).type === 'function' && (child as FNode).key !== undefined) {
                usedKeys.add((child as FNode).key)
            }
        })

        // KEY FIX: Remove instances with explicit keys that won't be rendered BEFORE rendering new ones
        // This ensures old route components are removed before new ones are created
        oldKeys.forEach(key => {
            // Only handle explicit keys (not auto-generated ones)
            if (typeof key !== 'string' || !key.startsWith('__auto_')) {
                if (!usedKeys.has(key)) {
                    removeInstanceByKey(effectiveRegistryParent, key)
                }
            }
        })

        // Now render children
        const nodes: Node[] = []
        fnode.forEach((child) => {
            const result = renderNode(child, parent, registryParent)
            if (result) {
                if (Array.isArray(result)) {
                    nodes.push(...result)
                } else {
                    nodes.push(result)
                }
            }
        })

        // Collect keys after render to handle auto-generated keys
        const newKeys = new Set<unknown>(registry.keys())

        // Remove auto-generated keys that are no longer present
        oldKeys.forEach(key => {
            if (!newKeys.has(key) && typeof key === 'string' && key.startsWith('__auto_')) {
                const instance = registry.get(key)
                if (instance) {
                    removeComponentInstance(instance)
                    registry.delete(key)
                }
            }
        })

        return nodes
    }

    if (typeof fnode === 'object') {
        if (typeof fnode.type === 'string') {
            const dom = document.createElement(fnode.type)

            if (fnode.props) {
                Object.entries(fnode.props).forEach(([key, value]) => {
                    if (key === 'ref') {
                        // Handle both callback refs and RefObject
                        if (typeof value === 'function') {
                            value(dom)
                        } else if (value && typeof value === 'object' && 'current' in value) {
                            value.current = dom
                        }
                    } else if (key.startsWith('on') && typeof value === 'function') {
                        const eventName = key.slice(2).toLowerCase()
                        dom.addEventListener(eventName, value as EventListener)

                        const domWithHandlers = dom as Element & { __eventHandlers?: Record<string, EventListener> }
                        if (!domWithHandlers.__eventHandlers) {
                            domWithHandlers.__eventHandlers = {}
                        }
                        domWithHandlers.__eventHandlers[eventName] = value as EventListener
                    } else if (key !== 'ref') {
                        setAttribute(dom, key, value)
                    }
                })
            }

            if (fnode.children && fnode.children.length > 0) {
                fnode.children.forEach((child: FNodeChild) => {
                    renderNode(child, dom)
                })
            }

            parent.appendChild(dom)
            return dom
        }

        if (typeof fnode.type === 'function') {
            return renderComponent(fnode, parent, registryParent)
        }
    }

    return null
}

function setAttribute(el: Element, key: string, value: unknown) {
    if (value === null || value === undefined) {
        el.removeAttribute(key)
    } else if (key === 'style' && typeof value === 'object') {
        Object.assign((el as HTMLElement).style, value)
    } else if (key === 'class') {
        el.className = String(value)
    } else if (key in el && typeof (el as unknown as Record<string, unknown>)[key] !== 'function') {
        (el as unknown as Record<string, unknown>)[key] = value
    } else {
        el.setAttribute(key, String(value))
    }
}

// Reconciliation helpers
function canReuse(oldNode: Node, newNode: Node): boolean {
    if (oldNode.nodeType !== newNode.nodeType) return false
    if (oldNode.nodeType === Node.ELEMENT_NODE && newNode.nodeType === Node.ELEMENT_NODE) {
        return (oldNode as Element).tagName === (newNode as Element).tagName
    }
    return true
}

function updateAttributes(oldEl: Element, newEl: Element): void {
    const oldAttrs = Array.from(oldEl.attributes)
    oldAttrs.forEach(attr => {
        if (!newEl.hasAttribute(attr.name)) {
            oldEl.removeAttribute(attr.name)
        }
    })

    const newAttrs = Array.from(newEl.attributes)
    newAttrs.forEach(attr => {
        if (oldEl.getAttribute(attr.name) !== attr.value) {
            if (attr.name === 'value' && 'value' in oldEl) {
                (oldEl as HTMLInputElement).value = attr.value
            } else if (attr.name === 'checked' && 'checked' in oldEl) {
                (oldEl as HTMLInputElement).checked = attr.value === 'true' || attr.value === ''
            } else {
                oldEl.setAttribute(attr.name, attr.value)
            }
        }
    })

    type ElWithHandlers = Element & { __eventHandlers?: Record<string, EventListener> }
    const oldHandlers = (oldEl as ElWithHandlers).__eventHandlers || {}
    const newHandlers = (newEl as ElWithHandlers).__eventHandlers || {}

    Object.keys(oldHandlers).forEach(event => {
        if (!newHandlers[event]) {
            oldEl.removeEventListener(event, oldHandlers[event])
        }
    })

    Object.keys(newHandlers).forEach(event => {
        if (oldHandlers[event] !== newHandlers[event]) {
            if (oldHandlers[event]) {
                oldEl.removeEventListener(event, oldHandlers[event])
            }
            oldEl.addEventListener(event, newHandlers[event])
        }
    })

    if (Object.keys(newHandlers).length > 0) {
        (oldEl as ElWithHandlers).__eventHandlers = newHandlers
    } else {
        delete (oldEl as ElWithHandlers).__eventHandlers
    }

    if (oldEl instanceof HTMLInputElement && newEl instanceof HTMLInputElement) {
        if (oldEl.value !== newEl.value) {
            oldEl.value = newEl.value
        }
        if (oldEl.checked !== newEl.checked) {
            oldEl.checked = newEl.checked
        }
    }
    if (oldEl instanceof HTMLTextAreaElement && newEl instanceof HTMLTextAreaElement) {
        if (oldEl.value !== newEl.value) {
            oldEl.value = newEl.value
        }
    }
    if (oldEl instanceof HTMLSelectElement && newEl instanceof HTMLSelectElement) {
        if (oldEl.value !== newEl.value) {
            oldEl.value = newEl.value
        }
    }
}

function reconcileChildren(oldEl: Element, newEl: Element): void {
    const oldChildren = Array.from(oldEl.childNodes)
    const newChildren = Array.from(newEl.childNodes)
    const maxLen = Math.max(oldChildren.length, newChildren.length)

    for (let i = 0; i < maxLen; i++) {
        const oldChild = oldChildren[i]
        const newChild = newChildren[i]

        if (!oldChild && newChild) {
            oldEl.appendChild(newChild)
        } else if (oldChild && !newChild) {
            oldEl.removeChild(oldChild)
        } else if (oldChild && newChild) {
            // patchNode returns the node that's now in DOM
            // For reconcileChildren, we just need the side effect
            patchNode(oldChild, newChild, oldEl)
        }
    }
}

type NodeWithOwner = Node & { __ownerInstance?: DOMComponentInstance }

function patchNode(oldNode: Node, newNode: Node, parent: Element): Node {
    if (canReuse(oldNode, newNode)) {
        const ownerInstance = (newNode as NodeWithOwner).__ownerInstance
        if (ownerInstance) {
            const idx = ownerInstance.nodes.indexOf(newNode)
            if (idx !== -1) {
                ownerInstance.nodes[idx] = oldNode
            }
            ;(oldNode as NodeWithOwner).__ownerInstance = ownerInstance
            delete (newNode as NodeWithOwner).__ownerInstance
        }

        if (oldNode.nodeType === Node.TEXT_NODE) {
            if (oldNode.nodeValue !== newNode.nodeValue) {
                oldNode.nodeValue = newNode.nodeValue
            }
        } else if (oldNode.nodeType === Node.ELEMENT_NODE) {
            updateAttributes(oldNode as Element, newNode as Element)
            reconcileChildren(oldNode as Element, newNode as Element)
        }
        return oldNode
    } else {
        // Ensure oldNode is actually a child of parent before replacing
        if (oldNode.parentNode === parent) {
            parent.replaceChild(newNode, oldNode)
        } else if (oldNode.parentNode) {
            // oldNode is attached to a different parent, remove it and insert newNode
            oldNode.parentNode.removeChild(oldNode)
            parent.insertBefore(newNode, null)
        } else {
            // oldNode is detached, just insert newNode
            parent.insertBefore(newNode, null)
        }
        return newNode
    }
}

export function reconcile(oldNodes: Node[], newNodes: Node[], parent: Node, beforeMarker: Node): Node[] {
    const resultNodes: Node[] = []
    const oldLen = oldNodes.length
    const newLen = newNodes.length

    // 1. Remove trailing old nodes (those past the new length). Range-batched
    //    when the tail is contiguous, which it almost always is.
    if (newLen < oldLen) {
        const toRemove: Node[] = []
        for (let i = newLen; i < oldLen; i++) {
            const old = oldNodes[i]
            if (old && old.parentNode) toRemove.push(old)
        }
        if (toRemove.length > 0) {
            batchRemoveRange(parent, toRemove)
            for (let i = 0; i < toRemove.length; i++) {
                _patchBuffer.push(OP_REMOVE, 0, 0, 0)
            }
        }
    }

    // 2. Walk overlap + the new-only suffix. Patch in-place where possible,
    //    accumulate insertions and flush them as a DocumentFragment whenever
    //    the run is broken (or at the end).
    const overlap = Math.min(oldLen, newLen)
    const toInsert: Node[] = []

    const flushInsert = (anchor: Node | null) => {
        if (toInsert.length === 0) return
        const count = toInsert.length
        batchInsertBefore(parent, toInsert, anchor)
        for (let i = 0; i < count; i++) {
            _patchBuffer.push(OP_INSERT_ELEMENT, 0, 0, 0)
        }
        toInsert.length = 0
    }

    // 2a. Overlapping range — patch in-place, no insertions to batch.
    for (let i = 0; i < overlap; i++) {
        const oldNode = oldNodes[i]
        const newNode = newNodes[i]
        const resultNode = patchNode(oldNode, newNode, parent as Element)
        resultNodes.push(resultNode)
    }

    // 2b. Trailing inserts (newLen > oldLen). Stage all and flush once before
    //     the beforeMarker.
    for (let i = overlap; i < newLen; i++) {
        const newNode = newNodes[i]
        if (newNode) {
            toInsert.push(newNode)
            resultNodes.push(newNode)
        }
    }
    flushInsert(beforeMarker)

    return resultNodes
}

export function render(app: FNodeChild | (() => FNodeChild), container: HTMLElement): () => void {
    container.innerHTML = ''

    const fnode: FNodeChild = typeof app === 'function'
        ? { type: app, props: {}, children: [], key: undefined }
        : app

    renderNode(fnode, container)

    // Return dispose function
    return () => {
        // Remove all component instances registered under this container
        if (instanceRegistry.has(container)) {
            const registry = instanceRegistry.get(container)!
            registry.forEach(instance => {
                removeComponentInstance(instance)
            })
            registry.clear()
            instanceRegistry.delete(container)
        }
        // Clear container
        container.innerHTML = ''
    }
}
